import { create } from 'zustand';
import { fusionPtrService } from '../services/fusionPtrService';

export const MAX_FUSION_ANALYSES = 10;

const useFusionPtrStore = create((set, get) => ({
  analyses: [],
  analysesLoading: false,
  selectedIds: [],
  snapshots: [],
  collectLoading: false,
  // Per-analysis snapshot cache (id -> snapshot): filled on demand by the
  // details modal via ensureSnapshot, reused by collect-all. Session-fresh
  // by design.
  snapshotCache: {},
  snapshotLoading: {},
  snapshotErrors: {},
  fusions: [],
  fusionsLoading: false,
  currentFusion: null,
  error: null,

  loadAnalyses: async () => {
    set({ analysesLoading: true, error: null });
    try {
      const response = await fusionPtrService.getAnalyses(1, 100);
      const list = response.data || response || [];
      // Archived analyses cannot be fused (backend enforces too)
      set({ analyses: (Array.isArray(list) ? list : []).filter((a) => a.status !== 'archived'), analysesLoading: false });
      // Drop selections that are no longer in the visible list (deleted/archived/off-page)
      // so the counter can never disagree with the highlighted cards.
      get().pruneSelection();
    } catch (error) {
      set({ error: error.message, analysesLoading: false });
    }
  },

  // Intersect the selection with the loaded analyses; returns dropped ids.
  pruneSelection: () => {
    const { selectedIds, analyses } = get();
    const valid = new Set((analyses || []).map((a) => String(a._id || a.id)));
    const kept = selectedIds.filter((id) => valid.has(String(id)));
    const dropped = selectedIds.filter((id) => !valid.has(String(id)));
    if (dropped.length) set({ selectedIds: kept });
    return dropped;
  },

  // Replace the selection with the given ids, keeping only loadable analyses
  // (used when re-creating a fusion whose sources may have vanished).
  // Returns kept ids plus dropped ones (unknown or over the cap).
  reselect: (ids) => {
    const { analyses } = get();
    const valid = new Set((analyses || []).map((a) => String(a._id || a.id)));
    const wanted = [...new Set((ids || []).map(String))];
    const loadable = wanted.filter((id) => valid.has(id));
    const dropped = [...wanted.filter((id) => !valid.has(id)), ...loadable.slice(MAX_FUSION_ANALYSES)];
    const kept = loadable.slice(0, MAX_FUSION_ANALYSES);
    set({ selectedIds: kept, snapshots: [], currentFusion: null });
    return { kept, dropped };
  },

  toggleSelect: (id) => {
    const { selectedIds } = get();
    const strId = String(id);
    if (selectedIds.includes(strId)) {
      set({ selectedIds: selectedIds.filter((s) => s !== strId) });
    } else if (selectedIds.length < MAX_FUSION_ANALYSES) {
      set({ selectedIds: [...selectedIds, strId] });
    }
  },

  clearSelection: () => set({ selectedIds: [], snapshots: [] }),

  collect: async () => {
    const { selectedIds, snapshotCache } = get();
    if (!selectedIds.length) return;
    set({ collectLoading: true, error: null });
    try {
      // Reuse modal-fetched snapshots; only fetch what's missing.
      const missing = selectedIds.filter((id) => !snapshotCache[String(id)]);
      let fresh = [];
      if (missing.length) {
        const response = await fusionPtrService.collect(missing);
        fresh = response.data?.snapshots || [];
      }
      const cache = { ...get().snapshotCache };
      fresh.forEach((s) => { cache[String(s.analysisId)] = s; });
      set({
        snapshotCache: cache,
        snapshots: selectedIds.map((id) => cache[String(id)]).filter(Boolean),
        collectLoading: false,
      });
    } catch (error) {
      set({ error: error.message, collectLoading: false });
    }
  },

  // Fetch one analysis snapshot on demand (details modal), cached per session.
  // Returns the snapshot, or null on failure (error stored per id).
  ensureSnapshot: async (id) => {
    const key = String(id);
    const { snapshotCache, snapshotLoading } = get();
    if (snapshotCache[key]) return snapshotCache[key];
    if (snapshotLoading[key]) return null;
    set((s) => ({
      snapshotLoading: { ...s.snapshotLoading, [key]: true },
      snapshotErrors: { ...s.snapshotErrors, [key]: null },
    }));
    try {
      const response = await fusionPtrService.collect([key]);
      const snap = response.data?.snapshots?.[0] || null;
      if (!snap) throw new Error('Empty snapshot');
      set((s) => ({
        snapshotCache: { ...s.snapshotCache, [key]: snap },
        snapshotLoading: { ...s.snapshotLoading, [key]: false },
      }));
      return snap;
    } catch (error) {
      set((s) => ({
        snapshotLoading: { ...s.snapshotLoading, [key]: false },
        snapshotErrors: { ...s.snapshotErrors, [key]: error.message },
      }));
      return null;
    }
  },

  loadFusions: async () => {
    set({ fusionsLoading: true });
    try {
      const response = await fusionPtrService.getFusions();
      set({ fusions: response.data || [], fusionsLoading: false });
    } catch (error) {
      set({ error: error.message, fusionsLoading: false });
    }
  },

  openFusion: async (id) => {
    set({ collectLoading: true, error: null });
    try {
      const response = await fusionPtrService.getFusion(id);
      // Note: page-level `snapshots` (collect-all view) is left untouched so
      // opening a fusion never duplicates the holistic view or save box.
      set({
        currentFusion: response.data?.fusion || null,
        collectLoading: false,
      });
    } catch (error) {
      set({ error: error.message, collectLoading: false });
    }
  },

  closeFusion: () => set({ currentFusion: null, snapshots: [] }),

  clearError: () => set({ error: null }),
}));

export default useFusionPtrStore;
